// Omnix/client/ui/CampaignManager.js
import { ComponentBase } from "../core/ComponentBase.js";

export class CampaignManager extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataCrud = null;
    }

    async initialize() {
        this.dataCrud = await this.createManagedComponent("DataCrud", null, {
            instanceId: `${this.config.instanceId}-crud`,
            rpcClass: "CampaignRpc",
            schemaPath: "collections/omniCampaigns",
            title: "Campaigns",
            viewConfig: {
                mode: 'grid',
                fields: [
                    { name: 'name', label: 'Campaign Name' },
                    {
                        name: 'status',
                        label: 'Status',
                        render: (val) => {
                            const statusColors = {
                                draft: 'secondary', scheduled: 'info', running: 'primary',
                                completed: 'success', failed: 'danger', cancelled: 'warning'
                            };
                            const color = statusColors[val] || 'light';
                            return `<span class="badge bg-${color} text-dark">${this.t(val)}</span>`;
                        }
                    },
                    {
                        name: 'stats',
                        label: 'Progress',
                        render: (stats) => stats ? `${stats.sent || 0} / ${stats.total || 0}` : '0 / 0'
                    },
                    {
                        name: 'scheduledAt',
                        label: 'Scheduled For',
                        render: (val, item) => {
                            if (item.status === 'draft' || item.status === 'cancelled') return 'Not Scheduled';
                            // --- THIS IS THE FIX ---
                            if (item.scheduleType === 'now' && item.stats?.startedAt) return `Immediate (Sent ${new Date(item.stats.startedAt).toLocaleDateString()})`;
                            // --- END OF FIX ---
                            return val ? new Date(val).toLocaleString() : 'Immediate';
                        }
                    },
                    {
                        name: 'stats.completedAt',
                        label: 'Completed On',
                        render: (val) => val ? new Date(val).toLocaleString() : 'N/A'
                    }
                ],
                actions: [
                    {
                        label: 'Start / Schedule',
                        icon: 'bi-play-fill',
                        eventName: 'startCampaign',
                        className: 'btn-outline-success',
                        visibleWhen: (item) => ['draft', 'cancelled'].includes(item.status)
                    },
                    {
                        label: 'Clone',
                        icon: 'bi-copy',
                        eventName: 'cloneCampaign',
                        className: 'btn-outline-secondary'
                        // No visibleWhen means it's always visible
                    },

                    {
                        label: 'Cancel',
                        icon: 'bi-stop-circle-fill',
                        eventName: 'cancelCampaign',
                        className: 'btn-outline-danger',
                        visibleWhen: (item) => ['scheduled', 'running'].includes(item.status)
                    },
                    {
                        label: 'View Statistics',
                        icon: 'bi-bar-chart-line-fill',
                        eventName: 'viewStatistics',
                        className: 'btn-outline-info',
                        visibleWhen: (item) => ['running', 'completed', 'failed'].includes(item.status)
                    }
                ],
                toolbar: { create: { enabled: true, label: "New Campaign" } }
            },
            formConfig: {
                fields: {
                    name: { type: 'text', label: 'Campaign Name', required: true },
                    type: {
                        type: 'select', label: 'Campaign Type', required: true,
                        options: [
                            { value: 'message', label: 'Message (Email, SMS, etc.)' },
                            { value: 'voice', label: 'Voice Broadcast' }
                        ],
                        default: 'message'
                    },

                    brandingKitId: {
                        type: 'BrandingKitSelector',
                        component: 'BrandingKitSelector',
                        label: 'Branding Kit',
                        visibleWhen: (data) => data.type === 'message' // Only for message campaigns
                    },

                    // --- Message-specific fields ---
                    channelConfigId: {
                        type: 'reference',
                        label: 'Send From (Channel)',
                        required: true,
                        reference: {
                            collection: 'omniChannelConfigs',
                            valueField: '_id',
                            displayField: 'name',
                        },
                        visibleWhen: (data) => data.type === 'message'
                    },
                    audienceId: {
                        type: 'reference',
                        label: 'Audience',
                        required: true,
                        reference: {
                            collection: 'omniAudiences',
                            valueField: '_id',
                            displayField: 'name'
                        }
                    },
                    templateId: {
                        type: 'reference',
                        label: 'Message Template',
                        required: true,
                        reference: {
                            collection: 'omniTemplates',
                            valueField: '_id',
                            displayField: 'name',
                            filter: (formData) => {
                                if (formData.channelConfigId) {
                                    return { channelConfigId: formData.channelConfigId };
                                }
                                return { _id: null };
                            }
                        },
                        dependsOn: ['channelConfigId'],
                        visibleWhen: (data) => data.type === 'message'
                    },
                    trackInInbox: {
                        type: 'boolean',
                        label: 'Create or update conversation in Unified Inbox',
                        default: true,
                        visibleWhen: (data) => data.type === 'message'
                    },
                    // --- Voice-specific field ---
                    voiceOptions: {
                        type: 'VoiceOptionsField',
                        component: 'VoiceOptionsField',
                        label: 'Voice Campaign Configuration',
                        required: true,
                        visibleWhen: (data) => data.type === 'voice'
                    },
                    // --- Scheduling fields (common to both) ---
                    scheduleType: {
                        type: 'select', label: 'Schedule', required: true,
                        options: [{ value: 'now', label: 'Send Immediately' }, { value: 'later', label: 'Schedule for Later' }]
                    },
                    scheduledAt: {
                        type: 'datetime-local', label: 'Scheduled Time',
                        visibleWhen: (data) => data.scheduleType === 'later'
                    }
                },
                dataConfig: { autoLoad: false }
            }
        });

        this.dataCrud.dataView.on(`startCampaign`, (item) => this.handleStart(item));
        this.dataCrud.dataView.on(`cancelCampaign`, (item) => this.handleCancel(item));
        this.dataCrud.dataView.on(`viewStatistics`, (item) => this.handleViewStatistics(item));
        this.dataCrud.dataView.on(`cloneCampaign`, (item) => this.handleClone(item));

        await super.initialize();
    }

    async handleViewStatistics(item) {
        const stats = item.stats || {};
        const message = `
            <p><strong>Campaign:</strong> ${item.name}</p>
            <ul class="list-unstyled text-start">
                <li><strong>Total Recipients:</strong> ${stats.total || 0}</li>
                <li><strong>Sent:</strong> ${stats.sent || 0}</li>
                <li><strong>Failed:</strong> ${stats.failed || 0}</li>
                <li><strong>Started:</strong> ${stats.startedAt ? new Date(stats.startedAt).toLocaleString() : 'N/A'}</li>
                <li><strong>Completed:</strong> ${stats.completedAt ? new Date(stats.completedAt).toLocaleString() : 'N/A'}</li>
            </ul>
        `;
        this.appClient.notifier.alert(message, { title: "Campaign Statistics" });
    }

    async handleStart(item) {
        try {
            await this.appClient.rpcClient.call('CampaignRpc.schedule', { campaignId: item._id });
            this.appClient.notifier.success(`Campaign "${item.name}" has been scheduled.`);
            this.dataCrud.refreshData();
        } catch (error) {
            this.appClient.notifier.error(`Failed to schedule campaign: ${error.message}`);
        }
    }

    async handleCancel(item) {
        const confirmed = await this.appClient.notifier.confirm(`Are you sure you want to cancel the campaign "${item.name}"?`);
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('CampaignRpc.cancel', { campaignId: item._id });
                this.appClient.notifier.info(`Campaign "${item.name}" has been cancelled.`);
                this.dataCrud.refreshData();
            } catch (error) {
                this.appClient.notifier.error(`Failed to cancel campaign: ${error.message}`);
            }
        }
    }

    handleClone(itemToClone) {
        this.log.info(`Cloning campaign: ${itemToClone.name}`);

        // 1. Create a copy of the data
        const clonedData = { ...itemToClone };

        // 2. Remove or reset fields that should not be carried over
        delete clonedData._id; // CRITICAL: This makes it a new item
        clonedData.name = `${itemToClone.name} (Copy)`;
        clonedData.status = 'draft'; // Reset status to draft
        clonedData.stats = {}; // Clear statistics
        clonedData.scheduleId = null;
        clonedData.scheduledAt = null;

        // 3. Call the new method on DataCrud to show the form with this data
        if (this.dataCrud) {
            this.dataCrud.showCreateFormWithData(clonedData);
        }
    }


    async initAfterLogin() {
        if (this.dataCrud) {
            this.dataCrud.refreshData();
        }
    }

    async render() {
        return await this.dataCrud.render();
    }
}